import { monthNames } from '../../i18n/calendarLabels';
import { localizeSystemText } from '../../i18n/messages';
import React from "react";
import PropTypes from "prop-types";
import SearchableSelect from "../common/SearchableSelect";
import {
  PAYMENT_WATCHDOG_FOLLOW_UP_STATUS_OPTIONS,
  PAYMENT_WATCHDOG_PRIORITY_OPTIONS,
} from "./clientSettlementWatchdog";
import { useTranslation } from 'react-i18next';

function ClientSettlementsReportForm({
  month,
  monthOptions,
  clientId,
  clientOptions,
  showDraft,
  showApproved,
  showNegativeBalanceOnly,
  showEmptyClients,
  watchdog,
  onChange,
  onClearFilters,
}) {
  const { t: tr, i18n } = useTranslation();
  return (
    <div className="mb-4">
      <div className="form-row align-items-end">
        <div className="col-md-4">
          <SearchableSelect
            label={tr("manage:month_133")}
            name="month"
            value={month}
            onChange={onChange}
            options={monthOptions.map(option => ({ ...option, text: i18n.language === 'en' ? `${monthNames('en')[Number(option.value.slice(5, 7)) - 1]} ${option.value.slice(0, 4)}` : option.text }))}
            placeholder={tr("billing:selectAMonth_121")}
            isClearable={false}
          />
        </div>
        <div className="col-md-4">
          <SearchableSelect
            label={tr("manage:client_108")}
            name="clientId"
            value={clientId}
            onChange={onChange}
            options={clientOptions}
            placeholder={tr("billing:everyone_122")}
            clearValue="all"
          />
        </div>
      </div>
      <div className="form-row align-items-end mt-2">
        <div className="col-md-auto">
          <div className="form-group">
            <button
              type="button"
              className={`btn btn-sm mr-2 ${showDraft ? "btn-info" : "btn-outline-secondary"}`}
              aria-pressed={showDraft}
              onClick={() => onChange({ target: { name: "showDraft", value: !showDraft } })}
            >{tr("billing:draft_123")}</button>
            <button
              type="button"
              className={`btn btn-sm mr-2 ${showApproved ? "btn-info" : "btn-outline-secondary"}`}
              aria-pressed={showApproved}
              onClick={() => onChange({ target: { name: "showApproved", value: !showApproved } })}
            >{tr("billing:approved_124")}</button>
            <button
              type="button"
              className={`btn btn-sm mr-2 ${showNegativeBalanceOnly ? "btn-danger" : "btn-outline-secondary"}`}
              aria-pressed={showNegativeBalanceOnly}
              onClick={() => onChange({ target: { name: "showNegativeBalanceOnly", value: !showNegativeBalanceOnly } })}
            >{tr("billing:underpaidOnly_125")}</button>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary mr-2"
              aria-pressed={showEmptyClients}
              onClick={() => onChange({
                target: {
                  name: "showEmptyClients",
                  value: !showEmptyClients,
                },
              })}
            >
              {showEmptyClients ? tr("manage:extraHideClientsWithoutEntries_126") : tr("manage:extraAlsoShowClientsWithoutEntries_127")}
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-dark"
              onClick={onClearFilters}
            >{tr("billing:clearFilters_126")}</button>
          </div>
        </div>
      </div>
      <div className="form-row align-items-end mt-2">
        <div className="col-md-auto">
          <div className="form-group">
            <button
              type="button"
              className={`btn btn-sm mr-2 ${watchdog.enabledOnly ? "btn-danger" : "btn-outline-secondary"}`}
              aria-pressed={watchdog.enabledOnly}
              onClick={() => onChange({
                target: {
                  name: "watchdog.enabledOnly",
                  value: !watchdog.enabledOnly,
                },
              })}
            >{tr("billing:overdueOnly_127")}</button>
          </div>
        </div>
        <div className="col-md-2">
          <div className="form-group">
            <label htmlFor="watchdog-min-amount" className="d-block">{tr("billing:minimumOverdueAmount_128")}<input
                id="watchdog-min-amount"
                name="watchdog.minAmount"
                type="number"
                min="0"
                className="form-control form-control-sm"
                value={watchdog.minAmount}
                onChange={onChange}
              />
            </label>
          </div>
        </div>
        <div className="col-md-2">
          <div className="form-group">
            <label htmlFor="watchdog-min-overdue-days" className="d-block">{tr("billing:minimumDaysOverdue_129")}<input
                id="watchdog-min-overdue-days"
                name="watchdog.minOverdueDays"
                type="number"
                min="0"
                step="1"
                className="form-control form-control-sm"
                value={watchdog.minOverdueDays}
                onChange={onChange}
              />
            </label>
          </div>
        </div>
        <div className="col-md-2">
          <div className="form-group">
            <label htmlFor="watchdog-priority" className="d-block">{tr("billing:priority_130")}<select
                id="watchdog-priority"
                name="watchdog.priority"
                className="form-control form-control-sm"
                value={watchdog.priority}
                onChange={onChange}
              >
                <option value="">{tr("manage:all_270")}</option>
                {PAYMENT_WATCHDOG_PRIORITY_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>{localizeSystemText(option.text, tr)}</option>
                ))}
              </select>
            </label>
          </div>
        </div>
        <div className="col-md-2">
          <div className="form-group">
            <label htmlFor="watchdog-follow-up-status" className="d-block">{tr("billing:handlingStatus_101")}<select
                id="watchdog-follow-up-status"
                name="watchdog.followUpStatus"
                className="form-control form-control-sm"
                value={watchdog.followUpStatus}
                onChange={onChange}
              >
                <option value="">{tr("manage:all_270")}</option>
                {PAYMENT_WATCHDOG_FOLLOW_UP_STATUS_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>{localizeSystemText(option.text, tr)}</option>
                ))}
              </select>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}

ClientSettlementsReportForm.propTypes = {
  month: PropTypes.string.isRequired,
  monthOptions: PropTypes.array.isRequired,
  clientId: PropTypes.string.isRequired,
  clientOptions: PropTypes.array.isRequired,
  showDraft: PropTypes.bool.isRequired,
  showApproved: PropTypes.bool.isRequired,
  showNegativeBalanceOnly: PropTypes.bool.isRequired,
  showEmptyClients: PropTypes.bool.isRequired,
  watchdog: PropTypes.shape({
    enabledOnly: PropTypes.bool.isRequired,
    minAmount: PropTypes.string.isRequired,
    minOverdueDays: PropTypes.string.isRequired,
    priority: PropTypes.string.isRequired,
    followUpStatus: PropTypes.string.isRequired,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  onClearFilters: PropTypes.func.isRequired,
};

export default ClientSettlementsReportForm;
